import { useMe, useWorkspaces } from "api-hooks/index"
import { SlowRedirect } from "core/components/SlowRedirect"
import { ConsoleBrandLoader } from "core/components/ConsoleBrandLoader"
import Head from "next/head"
import { useIsAuthenticated } from "core/hooks"
import { useResolvedPreferredWorkspaceId } from "core/hooks/use-resolved-preferred-workspace-id"
import { usePreferredConsoleModeStore } from "core/hooks/use-preferred-console-mode-store"
import { useOrganizations } from "api-hooks/use-organizations"
import { useSaveOnboardingStep } from "api-hooks/use-onboarding"
import { useEffect, useRef } from "react"
import { getDeniedWorkspaceIds } from "core/lib/denied-workspaces"
import { useAuroraEnabled } from "dashboard/hooks/use-aurora-enabled"
import { decideHomeRedirect } from "src/auth/utils/decide-home-redirect"
import { SignInContainer } from "auth/containers/SignInContainer"

export const HomePage = () => {
  const isAuthenticated = useIsAuthenticated()
  const { data: user, isPending: isUserPending } = useMe()
  const {
    data: workspaces,
    isLoading: isLoadingWorkspaces,
    isError: workspacesError,
  } = useWorkspaces()
  const {
    data: organizations = [],
    isError: orgsError,
    isLoading: isLoadingOrgs,
  } = useOrganizations()
  const { preferredWorkspaceId } = useResolvedPreferredWorkspaceId()
  const preferredConsoleMode = usePreferredConsoleModeStore(
    (s) => s.preferredConsoleMode
  )
  const auroraEnabled = useAuroraEnabled()

  const needsOnboarding = (user as { needs_onboarding?: boolean } | undefined)
    ?.needs_onboarding

  // The onboarding wizard used to be the only caller that cleared
  // needs_onboarding (seam-connect's update_onboarding unsets it, stamps
  // onboarding_completed_at, and fires the signup Slack notification). With
  // the wizard removed, mark it complete on this first landing instead —
  // otherwise the flag stays true forever, which would keep routing the user
  // to the assistant and override their console-mode preference on every
  // visit to `/`.
  const saveOnboardingStep = useSaveOnboardingStep()
  const hasClearedOnboardingRef = useRef(false)
  const onboardingOrgId = organizations[0]?.organization_id
  useEffect(() => {
    if (needsOnboarding !== true || onboardingOrgId == null) return
    if (hasClearedOnboardingRef.current) return
    hasClearedOnboardingRef.current = true
    // Best-effort: a failure just means the user sees the assistant again on
    // their next visit to `/`, so there is nothing useful to surface here.
    saveOnboardingStep.mutate({
      organization_id: onboardingOrgId,
      // Opaque analytics step; the backend requires >= 1 and never regresses
      // last_completed_step. There are no questions left to count.
      step: 1,
      completed: true,
    })
  }, [needsOnboarding, onboardingOrgId, saveOnboardingStep])

  // If isAuthenticated, never redirect to /signin — show a spinner instead, to avoid redirect loops while user data loads.
  const redirect = decideHomeRedirect({
    isAuthenticated,
    user,
    isUserPending,
    workspaces,
    isLoadingWorkspaces,
    workspacesError,
    organizations,
    isLoadingOrgs,
    orgsError,
    preferredWorkspaceId,
    deniedWorkspaceIds: getDeniedWorkspaceIds(),
    auroraEnabled,
    preferredConsoleMode,
  })

  return (
    <>
      <Head>
        <link rel="shortcut icon" href="/favicon-light.svg" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      {!isAuthenticated ? (
        <SignInContainer />
      ) : redirect ? (
        <SlowRedirect to={redirect} />
      ) : (
        <ConsoleBrandLoader />
      )}
    </>
  )
}

export default HomePage
